import { type ManagerHubVisibilityParams, type UseManagerHubOptions } from './types'
import { PRODUCT_TYPE } from '../common/constants/productType'
import { addUtcCalendarMonths } from '../common/utils/dateUtils'
import { MANAGING_TEAMS_TOPIC_ID } from '../essentials/constants'
import { isLanguageEnglish } from '../intl/utils/isLanguageEnglish'
import {
  getManagerCoachingRemainingSessions,
  MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS,
  type ManagerCoachingSeatEligibility,
} from '../managerCoaching/seatEligibility'

import type { PutUserPropertyRequest } from '../../models/user/User'

export const MANAGER_HUB_PROMOTION_SIGNAL_PROPERTY_NAME = 'promotionSignal.managerHub'

export const isManagingTeamsTopicDisabled = (disableSelfCareTopics?: string): boolean =>
  disableSelfCareTopics?.split(';').some((topicId) => topicId.trim() === MANAGING_TEAMS_TOPIC_ID) ?? false

/** Hub visibility from configuration and demographics. Can be evaluated before sign-in. */
export const shouldShowManagerHub = ({
  isManagerHubEnabled,
  productType,
  isUserMinor = false,
  language,
  isEssentialsEnabled,
  disableSelfCareTopics,
}: ManagerHubVisibilityParams): boolean =>
  isManagerHubEnabled &&
  isEssentialsEnabled === true &&
  productType === PRODUCT_TYPE.WFMH &&
  !isUserMinor &&
  isLanguageEnglish(language ?? '') &&
  !isManagingTeamsTopicDisabled(disableSelfCareTopics)

export const createManagerHubPromotionSignalRequestData = (timestamp: number): PutUserPropertyRequest => ({
  name: MANAGER_HUB_PROMOTION_SIGNAL_PROPERTY_NAME,
  value: String(timestamp),
  classType: 'String',
})

export const parseManagerHubPromotionSignalPropertyValue = (value: unknown): number | undefined => {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return undefined
  }

  const timestamp = Number(value)
  return Number.isSafeInteger(timestamp) ? timestamp : undefined
}

export const isManagerHubPromotionSignalActive = (managerHubTimestamp: unknown, now: number = Date.now()): boolean => {
  if (
    typeof managerHubTimestamp !== 'number' ||
    !Number.isSafeInteger(managerHubTimestamp) ||
    managerHubTimestamp < 0 ||
    !Number.isSafeInteger(now) ||
    now < 0 ||
    managerHubTimestamp > now
  ) {
    return false
  }

  return now < addUtcCalendarMonths(managerHubTimestamp, 3)
}

/**
 * Preserve generic-card promotion for missing/ineligible seats and exhausted eligible seats.
 * Other eligible seats keep the default rank; loading and request failures never promote.
 */
export const canPromoteManagerHubBasedOnSeatEligibility = (seatEligibility?: ManagerCoachingSeatEligibility): boolean =>
  seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.MISSING_LICENSE ||
  seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.INELIGIBLE ||
  (seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.ELIGIBLE &&
    getManagerCoachingRemainingSessions(seatEligibility.license) === 0)

export interface ShouldFetchManagerCoachingSeatEligibilityParams {
  showManagerHub: boolean
  isUserLoggedIn: boolean
  lyraId?: string
  hasActiveManagerHubPromotionSignal: boolean
  requireSeatEligibility: boolean
}

export const shouldFetchManagerCoachingSeatEligibility = ({
  showManagerHub,
  isUserLoggedIn,
  lyraId,
  hasActiveManagerHubPromotionSignal,
  requireSeatEligibility,
}: ShouldFetchManagerCoachingSeatEligibilityParams): boolean =>
  showManagerHub && isUserLoggedIn && Boolean(lyraId) && (hasActiveManagerHubPromotionSignal || requireSeatEligibility)

/**
 * For app adapters: add signed-in identity to Hub visibility to determine member access,
 * then use promotion or an explicit request to decide whether to load coaching-seat eligibility.
 * Call before initializing the app's seat loader; this derives prerequisites rather than request progress.
 */
export const getLoggedInManagerHubState = ({
  isUserLoggedIn,
  lyraId,
  managerHubPromotionSignal,
  requireSeatEligibility = false,
  ...visibility
}: ManagerHubVisibilityParams &
  UseManagerHubOptions & {
    isUserLoggedIn: boolean
    lyraId?: string
    managerHubPromotionSignal: unknown
  }) => {
  const showManagerHub = shouldShowManagerHub(visibility)
  const hasActiveManagerHubPromotionSignal = isManagerHubPromotionSignalActive(managerHubPromotionSignal)

  return {
    hasActiveManagerHubPromotionSignal,
    shouldFetchSeatEligibility: shouldFetchManagerCoachingSeatEligibility({
      showManagerHub,
      isUserLoggedIn,
      lyraId,
      hasActiveManagerHubPromotionSignal,
      requireSeatEligibility,
    }),
  }
}
